/**
 * WP-2 H3/H4/H5/H6 — Bus-leg step-listener reaction dispatcher.
 *
 * Extracted from FunnelExperience so reactions run on EVERY page type
 * (FunnelExperience, LandingPage, PostPurchasePage) via one shared hook.
 *
 * - H3: single dispatcher, mounted on all surfaces.
 * - H4: `paramsFrom` on a run_action reaction is resolved from the triggering
 *   event payload and folded into the action config before dispatch.
 * - H5: per-listener lifecycle (shared with ListenerBinder) + envelope hop-count
 *   loop guard (dispatch_event → listener → dispatch_event chains are capped).
 * - H6: a listener subscribes to the plain, canonical ui.*, and compatible
 *   funnel.ui.* forms, so it matches regardless of which leg emitted.
 */

import { useEffect, useRef } from 'react';
import {
  LEGACY_FUNNEL_UI_TRANSPORT_PREFIX,
  StepListenerReactionSchema,
  normalizeEventName,
  toWireEventName,
} from '@trafilea/tss-lib-funnel-schema-ts';
import type { FunnelEventBus } from '../events/funnel-event-bus';
import type { TssEventEnvelope, UIEventPayload } from '../events/envelope';
import { isFunnelUiEventsEnabled } from '../events/feature-flag';
import { debugLogFunnelEvent } from '../events/debug-logger';
import { matchesListenerSource } from '../events/listener-source-correlation';
import { resolveEventParams } from '../events/params-from-resolver';
import { MAX_EVENT_HOPS } from '../handlers/dispatch-event';
import { funnelLogger } from '../logger';
import type { RuntimeStepAction, StepListener } from '../types';
import {
  hasListenerFired,
  markListenerFired,
  resolveListenerLifecycle,
} from './listener-lifecycle';
import { toRuntimeStepAction } from './reaction-action';

export interface StepReactionHandlers {
  onAccept: () => void | boolean | Promise<void | boolean>;
  onDecline: () => void | boolean | Promise<void | boolean>;
  onGoToStep: (
    targetStepId: string | undefined,
    listener: StepListener
  ) => void | boolean | Promise<void | boolean>;
  /** hopCount = the triggering envelope's hop, threaded into the executor ctx (H5 loop guard). */
  onRunAction: (
    action: RuntimeStepAction,
    listener: StepListener,
    hopCount: number,
    intentEnvelope: TssEventEnvelope<UIEventPayload>
  ) => void;
}

export interface UseStepReactionsParams {
  eventBus: FunnelEventBus;
  stepId: string | null | undefined;
  listeners: StepListener[] | undefined;
  triggerLifecycle?: string;
  handlers: StepReactionHandlers;
  /** Overrides the master flag (tests). */
  enabled?: boolean;
}

function foldParamsFrom(action: RuntimeStepAction, envelope: TssEventEnvelope): RuntimeStepAction {
  const config = action.config as Record<string, unknown>;
  const paramsFrom = (config.paramsFrom ?? config.params_from) as
    | Record<string, string>
    | undefined;
  if (!paramsFrom) return action;

  const payload = envelope.payload as UIEventPayload | undefined;
  const eventParams = payload?.params ?? {};
  const resolved = resolveEventParams(
    config.params as Record<string, unknown> | undefined,
    paramsFrom,
    { value: (eventParams as Record<string, unknown>).value, eventMeta: eventParams }
  );

  return {
    ...action,
    config: { ...action.config, ...resolved },
  } as RuntimeStepAction;
}

export function useStepReactions(params: UseStepReactionsParams): void {
  const { eventBus, stepId, listeners, triggerLifecycle = 'session', handlers } = params;
  const enabled = params.enabled ?? isFunnelUiEventsEnabled();

  // Keep the latest handlers callable without re-subscribing the bus.
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  const navigationInFlightRef = useRef(new Set<string>());

  useEffect(() => {
    if (!enabled || !listeners?.length || !stepId) return undefined;

    const dispatch = (listener: StepListener, envelope: TssEventEnvelope): void => {
      // Multiple controls may emit the same command. A generated listener
      // executes only for the source block/trigger that owns its managed
      // behavior; legacy listeners without source fields stay compatible.
      if (!matchesListenerSource(listener, envelope)) {
        return;
      }
      // A handler's result is observable but can never re-activate the same
      // command listener. This is the intent → result loop guard.
      if (
        envelope.metadata.phase === 'result' &&
        normalizeEventName(envelope.name) === normalizeEventName(listener.eventName)
      ) {
        return;
      }
      // H5 loop guard: drop events that have hopped too many times.
      const hop = envelope.metadata.hopCount ?? 0;
      if (hop > MAX_EVENT_HOPS) {
        funnelLogger.warn('Step reaction dropped: event hop limit exceeded (loop guard)', {
          listenerId: listener.id,
          eventName: listener.eventName,
          stepId,
          hopCount: hop,
        });
        return;
      }

      // H5 lifecycle: honor the per-listener firing cap (shared with DOM leg).
      // A listener-level lifecycle (authored on the card) wins over the
      // step-level default.
      const lifecycle = resolveListenerLifecycle(listener.config, triggerLifecycle);
      if (hasListenerFired(stepId, listener.id, lifecycle)) return;

      const config = listener.config as Record<string, unknown>;
      const parsed = StepListenerReactionSchema.safeParse(config.reaction);
      if (!parsed.success) {
        // Subscribe-by-name telemetry hook (no executable reaction) — the event
        // already shipped via the emitter; nothing else to run.
        funnelLogger.info('Step listener fired without an executable reaction', {
          listenerId: listener.id,
          eventName: listener.eventName,
          stepId,
        });
        return;
      }

      const reaction = parsed.data;
      const current = handlersRef.current;
      debugLogFunnelEvent('listener-match', {
        eventName: listener.eventName,
        listenerId: listener.id,
        stepId,
        sourceEventId: envelope.metadata.eventId,
        sourcePhase: envelope.metadata.phase,
        reaction:
          reaction.kind === 'navigate' ? `navigate:${reaction.navigate?.type}` : 'run_action',
        hop,
      });

      if (reaction.kind === 'navigate' && reaction.navigate) {
        const eventPayload = envelope.payload as UIEventPayload | undefined;
        const runtimeNavigationHandled = eventPayload?.params?.runtimeNavigationHandled === true;
        if (
          runtimeNavigationHandled &&
          (reaction.navigate.type === 'accept' || reaction.navigate.type === 'decline')
        ) {
          debugLogFunnelEvent('listener-match', {
            eventName: listener.eventName,
            listenerId: listener.id,
            stepId,
            sourceEventId: envelope.metadata.eventId,
            sourcePhase: envelope.metadata.phase,
            reaction: `navigate:${reaction.navigate.type}:already-handled`,
            hop,
          });
          markListenerFired(stepId, listener.id, lifecycle);
          return;
        }

        const navigationKey = `${stepId}:${listener.id}`;
        if (navigationInFlightRef.current.has(navigationKey)) return;
        navigationInFlightRef.current.add(navigationKey);

        const executeNavigation = async (): Promise<void> => {
          try {
            let outcome: void | boolean = false;
            switch (reaction.navigate?.type) {
              case 'accept':
                outcome = await current.onAccept();
                break;
              case 'decline':
                outcome = await current.onDecline();
                break;
              case 'go_to_step':
                outcome = await current.onGoToStep(reaction.navigate.stepId, listener);
                break;
            }
            if (outcome !== false) {
              markListenerFired(stepId, listener.id, lifecycle);
            }
          } catch (error) {
            funnelLogger.warn('Step listener navigation failed; lifecycle remains retryable', {
              listenerId: listener.id,
              eventName: listener.eventName,
              stepId,
              error: error instanceof Error ? error.message : String(error),
            });
          } finally {
            navigationInFlightRef.current.delete(navigationKey);
          }
        };
        void executeNavigation();
        return;
      }

      if (reaction.kind === 'run_action' && reaction.action) {
        const runtimeAction = toRuntimeStepAction(
          reaction.action,
          `reaction-${listener.id ?? listener.eventName}`
        );
        // H4: resolve paramsFrom from the triggering event into the action config.
        const action = foldParamsFrom(runtimeAction, envelope);
        // T1: thread the triggering hop so dispatch_event re-emissions increment
        // from the REAL depth (executor ctx -> handler ctx.hopCount).
        current.onRunAction(action, listener, hop, envelope as TssEventEnvelope<UIEventPayload>);
      }
    };

    // H6 / ADR-0011: subscribe to plain, canonical wire, and legacy-compatible
    // forms. A single emission carries one name, so only one subscription fires
    // and no logical event is duplicated.
    const subs: Array<{ unsubscribe: () => void }> = [];
    const seenNames = new Set<string>();
    for (const listener of listeners) {
      const plainName = normalizeEventName(listener.eventName);
      for (const name of [
        plainName,
        toWireEventName(plainName),
        `${LEGACY_FUNNEL_UI_TRANSPORT_PREFIX}${plainName}`,
      ]) {
        const subKey = `${listener.id}:${name}`;
        if (seenNames.has(subKey)) continue;
        seenNames.add(subKey);
        subs.push(eventBus.on(name, (envelope) => dispatch(listener, envelope)));
      }
    }

    return () => {
      subs.forEach((s) => s.unsubscribe());
    };
  }, [eventBus, stepId, listeners, triggerLifecycle, enabled]);
}
